/**
 * Browser "save as" for a generated file, shared by every download flow
 * (anonymous CVs, CV bundles, offer reports).
 *
 * Kept dependency-free on purpose: the download API modules that use it also
 * pull `solid-sonner`, whose import touches `window` and would break node-env
 * unit tests — this module stays importable from anywhere.
 */

/**
 * Extracts the server-suggested (PII-free) filename from `Content-Disposition`,
 * falling back to a generic name when the header is absent.
 *
 * The forms are tried most-specific first. The previous single pattern
 * (`filename="?([^"]+)"?`) read the *first* `filename=` it found and, on an
 * unquoted value, let `[^"]+` swallow every parameter after it — so a header
 * carrying both forms yielded the RFC 5987 blob as the name, and an unquoted
 * one yielded the name plus whatever followed.
 *
 * Note this only ever runs when the header is actually readable: a
 * cross-origin response hides `Content-Disposition` from JS unless the server
 * lists it in `Access-Control-Expose-Headers`.
 */
export const parseAttachmentFilename = (
  disposition: string | undefined,
  fallback: string,
) => {
  if (!disposition) return fallback;

  const extended = disposition.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-encoding is not a reason to lose the download.
      return extended[1].trim();
    }
  }

  const quoted = disposition.match(/filename\s*=\s*"([^"]*)"/i);
  if (quoted?.[1]) return quoted[1];

  const bare = disposition.match(/filename\s*=\s*([^;]+)/i);
  return bare?.[1]?.trim() || fallback;
};

/** Object-URL → transient `<a download>` → click. */
export const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // WebKit can cancel the download silently if the object URL is revoked
  // before it has started reading the blob — the click already returned, so
  // there's no error to catch and the success toast fires with nothing saved.
  // Deferring the revoke gives the browser time to pick up the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
